import ClearIcon from "@mui/icons-material/Clear";
import { Avatar, Grid, IconButton, Tooltip } from "@mui/material";
import * as exifr from "exifr";
import * as faceapi from "face-api.js";
import React, { useContext, useEffect } from "react";
import FileBase64 from "react-file-base64";
import { useTranslation } from "react-i18next";
import cameraplus2 from "../../assets/AddPhotoIcon.svg";
import cameraIcon from "../../assets/cameraIcon.svg";
import Toast from "../../components/ToastContainer/CustomToast";
import { loadFaceModel } from "../../Pages/Employee/faceLoader";
import { ThemeContext } from "../../theme/ThemeContext";

export default function ConfirmUploadPhoto(props) {
  const [profile, setProfile] = React.useState(cameraplus2);
  const [file, setFile] = React.useState({});
  const { t } = useTranslation();
  const { darkMode } = useContext(ThemeContext);

  useEffect(() => {
    loadFaceModel();
  }, []);
  // Reset
  React.useEffect(() => {
    if (props?.resetTrigger !== undefined && props?.resetTrigger !== null) {
      setProfile("");
      props.onsetProfile("");
      setFile({});
    }
  }, [props?.resetTrigger]);

  // Handle Upload
  const handleProfile = async (file) => {
    console.log("Complete File Object:", file);
    console.log("Type:", file.type);
    console.log("Name:", file.name);
    console.log("File:", file.file);
    console.log("Base64:", file.base64?.substring(0, 50));
    if (
      file.type === "image/png" ||
      file.type === "image/jpeg" ||
      file.type === "image/jpg"
    ) {
      try {
        // Step 1: Fix image orientation
        const corrected = await fixImageOrientation(file.file);
        console.log("corrected:", corrected);

        // Step 2: Process the corrected image (FaceAPI crop)
        const processed = await processImage(corrected.base64);
        console.log("processed:", processed);

        // Step 3: Update state
        setProfile(processed);

        setFile({
          ...file,
          file: corrected.correctedFile,
        });

        // Step 4: Send corrected image to parent
        props.onsetProfile({
          ...file,
          file: corrected.correctedFile,
          base64: processed,
        });
      } catch (e) {
        console.error(e);
        Toast("Image processing failed", "error");
      }
    } else {
      setFile({});
      Toast(t("VALID_IMAGE_ERROR"), "error");
    }
  };

  // Smart Face Crop
  const processImage = async (dataUrl) => {
    const img = new Image();
    img.src = dataUrl;
    await new Promise((r) => (img.onload = r));

    const detection = await faceapi.detectSingleFace(
      img,
      new faceapi.TinyFaceDetectorOptions(),
    );

    const OUTPUT_SIZE = 400;

    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;

    const ctx = canvas.getContext("2d");

    if (!detection) {
      ctx.drawImage(img, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
      return canvas.toDataURL("image/jpeg", 1);
    }

    const { x, y, width, height } = detection.box;

    // Smart padding (best for all photos)
    const paddingTop = height * 0.5;
    const paddingBottom = height * 0.3;
    const paddingSide = width * 0.6;

    let sx = x - paddingSide;
    let sy = y - paddingTop;
    let sw = width + paddingSide * 2;
    let sh = height + paddingTop + paddingBottom;

    // boundary check
    if (sx < 0) sx = 0;
    if (sy < 0) sy = 0;

    if (sx + sw > img.width) sw = img.width - sx;
    if (sy + sh > img.height) sh = img.height - sy;

    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

    return canvas.toDataURL("image/jpeg", 1);
  };

  const fixImageOrientation = async (file) => {
    try {
      // ---------- First try createImageBitmap ----------
      if ("createImageBitmap" in window) {
        try {
          const bitmap = await createImageBitmap(file, {
            imageOrientation: "from-image",
          });

          const canvas = document.createElement("canvas");
          canvas.width = bitmap.width;
          canvas.height = bitmap.height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(bitmap, 0, 0);

          bitmap.close();

          return new Promise((resolve) => {
            canvas.toBlob(
              (blob) => {
                resolve({
                  correctedFile: new File([blob], file.name, {
                    type: "image/jpeg",
                  }),
                  base64: canvas.toDataURL("image/jpeg", 1),
                });
              },
              "image/jpeg",
              1,
            );
          });
        } catch (err) {
          console.log("createImageBitmap failed. Using EXIF fallback.");
        }
      }

      // ---------- Fallback to EXIF ----------
      const orientation = (await exifr.orientation(file)) || 1;

      return new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);

        img.onload = () => {
          console.log("Orientation =", orientation);
          console.log("Width =", img.width);
          console.log("Height =", img.height);
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          if ([5, 6, 7, 8].includes(orientation)) {
            canvas.width = img.height;
            canvas.height = img.width;
          } else {
            canvas.width = img.width;
            canvas.height = img.height;
          }

          switch (orientation) {
            case 2:
              ctx.translate(canvas.width, 0);
              ctx.scale(-1, 1);
              break;

            case 3:
              ctx.translate(canvas.width, canvas.height);
              ctx.rotate(Math.PI);
              break;

            case 4:
              ctx.translate(0, canvas.height);
              ctx.scale(1, -1);
              break;

            case 5:
              ctx.rotate(0.5 * Math.PI);
              ctx.scale(1, -1);
              ctx.translate(0, -img.height);
              break;

            case 6:
              ctx.rotate(0.5 * Math.PI);
              ctx.translate(0, -img.height);
              break;

            case 7:
              ctx.rotate(0.5 * Math.PI);
              ctx.scale(-1, 1);
              ctx.translate(-img.width, -img.height);
              break;

            case 8:
              ctx.rotate(-0.5 * Math.PI);
              ctx.translate(-img.width, 0);
              break;

            default:
              break;
          }

          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              URL.revokeObjectURL(objectUrl);

              resolve({
                correctedFile: new File([blob], file.name, {
                  type: "image/jpeg",
                }),
                base64: canvas.toDataURL("image/jpeg", 1),
              });
            },
            "image/jpeg",
            1,
          );
        };

        img.onerror = (e) => {
          URL.revokeObjectURL(objectUrl);
          reject(e);
        };

        img.src = objectUrl;
      });
    } catch (e) {
      console.error(e);
      return {
        correctedFile: file,
        base64: URL.createObjectURL(file),
      };
    }
  };

  const handleClearProfile = () => {
    setProfile("");
    setFile({});
    props.onsetProfile("");
  };

  return (
    <>
      <Grid
        container
        display={"flex"}
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <div
          style={{
            borderStyle: "dashed",
            borderRadius: "50%",
            padding: "5px",
            color: "#D2D2D2",
          }}
        >
          <Tooltip title={props.profilePhoto ? t("PROFILE_UPLOAD") : ""}>
            <IconButton
              variant="contained"
              component="label"
              style={{
                position: "relative",
                width: "210px",
                height: "210px",
                font: "normal normal normal 18px/24px Roboto",
                letterSpacing: "0px",
              }}
              disabled={props.disabled}
            >
              {props.profilePhoto ? (
                <Avatar
                  className="ImageContent"
                  alt=""
                  src={props.profilePhoto}
                  style={{
                    height: "200px",
                    width: "200px",
                    zIndex: "99",
                    borderRadius: "50%",
                  }}
                />
              ) : (
                <>
                  <Avatar
                    className="ImageContent"
                    alt=""
                    src={cameraIcon}
                    style={{
                      height: "100%",
                      width: "100%",
                      borderRadius: "50%",
                      backgroundColor: darkMode
                        ? "rgba(221, 221, 221, 0.15)"
                        : "#F4F6F8",
                    }}
                    sx={[
                      () => ({
                        "& .MuiAvatar-img": {
                          width: "49px",
                          height: "42px",
                          textAlign: "center",
                          objectFit: "cover",
                        },
                      }),
                    ]}
                  />

                  <div
                    style={{
                      fontSize: "16px",
                      position: "absolute",
                      top: "68%",
                      zIndex: "1",
                      color: "#C3C5C7",
                    }}
                  >
                    {t("PURPOSEPIC")}
                  </div>

                  <Grid style={{ display: "none" }}>
                    <FileBase64
                      multiple={false}
                      onDone={(file) => {
                        console.log("onDone called", file);
                        handleProfile(file);
                      }}
                    />
                  </Grid>
                </>
              )}
            </IconButton>
          </Tooltip>

          {props.profilePhoto && !props.disabled && (
            <Tooltip title={t("CLEAREPROFILEPHOTO")}>
              <div style={{ position: "relative", display: "inline-block" }}>
                <ClearIcon
                  style={{
                    color: "white",
                    backgroundColor: "#949596",
                    borderRadius: "50%",
                    position: "absolute",
                    marginTop: "50px",
                    marginLeft: "-22px",
                    transform: "translate(-50%, 50%)",
                    zIndex: 1,
                  }}
                  onClick={handleClearProfile}
                />
              </div>
            </Tooltip>
          )}
        </div>

        <Grid
          direction="row"
          display="flex"
          alignItems="flex-end"
          style={{
            color: darkMode ? "rgba(195, 197, 199, 1)" : "#C3C5C7",
            opacity: 1,
            fontSize: "12px",
            paddingTop: "34px",
            justifyContent: "center",
          }}
        >
          {t("PURPOSECON")}
        </Grid>
      </Grid>
    </>
  );
}

ConfirmUploadPhoto.defaultProps = {
  disabled: false,
};
